import {
  AspectRatio,
  Box,
  Card,
  Group,
  lighten,
  Space,
  Spoiler,
  Stack,
} from "@mantine/core";
import logoLight from "../assets/advize-logo-light.jpg";
import {
  IconChevronLeft,
  IconChevronRight,
  IconExternalLink,
  IconLink,
  IconSchool,
  IconStar,
  IconStarFilled,
  IconUser,
} from "@tabler/icons-react";
import { AdvizerVideo } from "@/components/AdvizerVideo";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { PartnerLogo } from "@/components/PartnerLogo";
import { AdvizerSearchControls } from "@/components/advizerSearchControls/AdvizerSearchControls";
import { useIsLaptopOrBigger, useStyles } from "@/styles/useStyles";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { AdvizerSearchDrawer } from "@/components/drawers/AdvizerSearchDrawer";
import { Button } from "@/components/ui/Button";
import {
  useAdvizer,
  useAdvizerQuotes,
  useFilteredAdvizers,
  usePartners,
} from "@/store/selectors";
import { AdvizerTag } from "@/components/AdvizerTag";
import { parsePersonalBrandingLinks } from "@/lib/helpers";
import { IAdvizerVideo } from "@/models/AdvizerVideo";
import { useStore } from "@/store";
import { IAdvizerModel } from "@/models/Advizer";
import { Image } from "@/components/ui/Image";
import { Text } from "@/components/ui/Text";
import { Title } from "@/components/ui/Title";
import { Navigation } from "@/services/Navigation";
import { IQuote } from "@/types/ApiTypes";
import { AdvizerQuotes } from "@/components/AdvizerQuotes";
import { ResourceConfigs } from "@/configs/resourceConfigs";
import { Analytics } from "@/services/Analytics";
import { useShowMBA } from "@/store/selectors/featureSelectors";
import { Badge } from "@/components/ui/Badge";
import { LawHubBanner } from "@/components/ads/LawHubBanner";
import { config } from "@/config";
import { CopyLink } from "@/components/playlists/CopyPlaylistLink";
import { useIsRecommendedAdvizer } from "@/store/selectors/userProfileSelectors";

const iconSize = 16;
const iconStyle = {
  width: 24,
};
const itemGroupProps: any = {
  gap: 8,
  flex: 1,
  align: "flex-start",
  justify: "flex-start",
  wrap: "nowrap",
  w: "100%",
  mb: 8,
};

const itemTitleProps: any = {
  titleStyle: "h5",
  c: "text.7",
  ta: "left",
  lh: 1.3,
  m: 0,
  fw: 500,
  w: "100%",
  fontFamily: "League Spartan, sans-serif",
};

export const AdvizerProfile = ({
  standalone,
  advizerIdFromProps,
  disableTags,
  noBottomPadding,
  showNextPrevButtons,
}: {
  standalone?: boolean;
  advizerIdFromProps?: string;
  disableTags?: boolean;
  noBottomPadding?: boolean;
  showNextPrevButtons?: boolean;
}) => {
  const { advizerId: advizerIdParam } = useParams();
  const isLaptopOrBigger = useIsLaptopOrBigger();
  const { theme } = useStyles();
  const [searchParams, setSearchParams] = useSearchParams();
  const questionNumberFromParams = Number(searchParams.get("question"));
  const watchRef = useRef<HTMLDivElement>(null);

  const advizerId = advizerIdParam || advizerIdFromProps || "";
  const advizer = useAdvizer(advizerId);

  const { advizers } = useFilteredAdvizers(); // TODO update to have specific selectors
  const activeQuestionId = useStore(
    (state) => state.advizerSearch.activeQuestionId,
  );
  const quotes = useAdvizerQuotes(advizerId || "");

  const showLawHubBanner = useMemo(() => {
    return false;
    if (standalone) return false;
    if (standalone) return false;
    return advizer?.tags?.some(
      (tag) =>
        tag.title.toLowerCase().includes("law") ||
        tag.title.toLowerCase().includes("legal"),
    );
  }, [advizer?.tags, standalone]);

  const { nextAdvizerId, prevAdvizerId } = useMemo(() => {
    if (!advizers || advizers.length === 0) {
      return { nextAdvizerId: undefined, prevAdvizerId: undefined };
    }

    const currentIndex = advizers.findIndex(
      (a: IAdvizerModel) => a.id === advizerId,
    );
    const lastIndex = advizers.length - 1;

    return {
      nextAdvizerId:
        currentIndex === lastIndex
          ? advizers[0].id
          : advizers[currentIndex + 1]?.id,
      prevAdvizerId:
        currentIndex === 0
          ? advizers[lastIndex].id
          : advizers[currentIndex - 1]?.id,
    };
  }, [advizerId, advizers]);

  useEffect(() => {
    if (standalone) return;
    const advizerIsInList = !!advizers?.find(
      (a: IAdvizerModel) => a.id === advizerId,
    );

    if (!advizerIsInList && advizers && advizers.length > 0) {
      const firstAdvizerId = advizers[0].id;
      if (!firstAdvizerId) return;
      Navigation.navigate(`/advizers/${firstAdvizerId}/profile`);
    }
  }, [advizerId, advizers, standalone]);

  useEffect(() => {
    if (!questionNumberFromParams || !advizer?.videos) return;

    if (isNaN(questionNumberFromParams)) return;

    // Wait for the DOM to render the videos
    const timer = setTimeout(() => {
      const targetVideoIndex = advizer.videos.findIndex(
        (video: IAdvizerVideo) =>
          video.questionNumber === questionNumberFromParams,
      );

      if (targetVideoIndex !== -1) {
        const videoElement = document.getElementById(
          `advizer-profile-video-${targetVideoIndex + 1}`,
        );

        if (videoElement && watchRef.current) {
          const containerTop = watchRef.current.offsetTop;
          const elementTop = videoElement.offsetTop;
          const offset = 50; // Adjust this value to fine-tune the scroll position

          watchRef.current.scrollTo({
            top: elementTop - containerTop - offset,
            behavior: "smooth",
          });

          Analytics.visitAdvizerVideoDirectly({
            advizerId,
            questionNumber: questionNumberFromParams,
          });
        }
        // setPulseBorderQuestionNumber(questionNumberFromParams);

        // setTimeout(() => {
        //   setPulseBorderQuestionNumber(null);
        // }, 1000);

        // setSearchParams({});
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [advizer?.videos, advizerId, questionNumberFromParams, setSearchParams]);

  const renderAdvizerWatch = useCallback(() => {
    return (
      <Stack w="100%" gap={50} p={0}>
        {advizer?.videos
          .filter((video: IAdvizerVideo) => {
            if (activeQuestionId) {
              return video.question?.id === activeQuestionId;
            }
            return true;
          })
          .map((video: IAdvizerVideo, i: number) => {
            const matchingQuote = quotes?.find(
              (quote: IQuote) => quote.videoId === video.id,
            );

            const autoplay = questionNumberFromParams === video.questionNumber;

            return (
              <Card
                id={`advizer-profile-video-${video.questionNumber}`}
                padding="md"
                radius="md"
                bg="transparent"
                w="100%"
                key={video.id}
              >
                <Card.Section pos="relative" h="100%" flex={1}>
                  <AdvizerVideo
                    key={i}
                    advizerId={advizerId}
                    videoId={video.id}
                    video={video}
                    questionNumber={video.questionNumber}
                    title={video.title}
                    url={video.url}
                    isAdvizerProfilePage={true}
                    forceNoLight={autoplay}
                    withControls={true}
                  />
                </Card.Section>
                <Card.Section>
                  <Space h="xs" />
                  <AdvizerQuotes
                    advizer={advizer}
                    activeQuote={matchingQuote}
                  />
                </Card.Section>
              </Card>
            );
          })}
      </Stack>
    );
  }, [activeQuestionId, advizer, advizerId, quotes, questionNumberFromParams]);

  // const PartnerLogos = useMemo(() => {
  //   if (!advizer?.partners || advizer?.partners.length === 0) {
  //     return null;
  //   }
  //   return (
  //     <Group justify="center" gap="xl">
  //       {(advizer?.partners || []).map((partner) => {
  //         return (
  //           <Group key={partner.id}>
  //             <PartnerLogo
  //               width={28}
  //               partnerId={partner.id}
  //               partnerSlug={partner.slug}
  //             />
  //             <Text size="sm" fw={500} c="text.8">
  //               {partner.title}
  //             </Text>
  //           </Group>
  //         );
  //       })}
  //     </Group>
  //   );
  // }, [advizer?.partners]);

  const AdvizerProfileImage = useMemo(() => {
    return (
      <Box
        bg="white"
        p={0}
        bd="1px solid primary.1"
        style={{ borderRadius: "50%" }}
      >
        <AspectRatio ratio={1}>
          <Image
            src={advizer?.profilePictureURL || logoLight}
            w={120}
            radius="50%"
            alt={`Advizer ${advizer?.firstName}'s profile picture`}
          />
        </AspectRatio>
      </Box>
    );
  }, [advizer?.firstName, advizer?.profilePictureURL]);

  const AdvizerNavWithAvatar = useMemo(() => {
    return (
      <Group w="100%" align="center" justify="center" wrap="wrap">
        {(!standalone || showNextPrevButtons) && (
          <Button
            ariaLabel="Go to previous advizer"
            to={`/advizers/${prevAdvizerId}`}
            leftSection={<IconChevronLeft />}
            variant="transparent"
            flex={1}
            c="primary.7"
            keepExistingUrlParams
          >
            Prev
          </Button>
        )}
        {AdvizerProfileImage}
        {(!standalone || showNextPrevButtons) && (
          <Button
            ariaLabel="Go to next advizer"
            to={`/advizers/${nextAdvizerId}`}
            rightSection={<IconChevronRight />}
            variant="transparent"
            flex={1}
            c="primary.7"
            keepExistingUrlParams
          >
            Next
          </Button>
        )}
      </Group>
    );
  }, [
    standalone,
    showNextPrevButtons,
    prevAdvizerId,
    AdvizerProfileImage,
    nextAdvizerId,
  ]);

  if (isLaptopOrBigger && !standalone) {
    return (
      <>
        {!standalone && <AdvizerSearchControls />}
        <Stack
          pl="md"
          pr="md"
          pt={standalone ? 0 : isLaptopOrBigger ? 120 : 90}
          // pb={200}
          align={"center"}
          w="100%"
          ta={"left"}
          gap={0}
          // maw={1200}
          mx="auto"
          // bg="background.0"
          h="100%"
          style={{ overflow: "hidden" }}
          bg={lighten(theme.colors.background[1], 0.5)}
        >
          <Group align="start" justify="center" w="100%" maw={1200} h="100%">
            <Stack
              pl="md"
              pr="md"
              pb={200}
              align={"center"}
              w="100%"
              maw={600}
              ta={"left"}
              flex={1}
              pos="sticky"
              top={0}
              style={{ overflow: "auto" }}
              mah="100%"
            >
              {showLawHubBanner && (
                <>
                  <LawHubBanner context="advizer_profile" />
                  <Space h="lg" />
                </>
              )}

              {AdvizerNavWithAvatar}
              <AdvizerProfileCard
                advizerId={advizerId}
                linkToProfilePage={standalone}
              />
            </Stack>
            <Stack
              ref={watchRef}
              w="100%"
              mx="auto"
              flex={1}
              h="100%"
              pb={500}
              style={{ overflow: "auto" }}
            >
              {renderAdvizerWatch()}
            </Stack>
          </Group>
        </Stack>
        {!standalone && <AdvizerSearchDrawer />}
      </>
    );
  }

  return (
    <>
      {!standalone && <AdvizerSearchControls />}
      {/* {renderAdvizerProfile()} */}
      <Stack
        // px="xs"
        pt={standalone ? 0 : isLaptopOrBigger ? 120 : 90}
        pb={noBottomPadding ? 0 : 200}
        align={"center"}
        w="100%"
        ta={"left"}
        gap={0}
        // maw={700}
        mx="auto"
        // bg="background.0"
        h="100%"
        style={{ overflow: "auto" }}
        bg={lighten(theme.colors.background[1], 0.5)}
      >
        <Stack gap={0} w="100%" maw={550} mx="auto">
          <>
            {showLawHubBanner && (
              <>
                <LawHubBanner context="advizer_profile" />
                <Space h="lg" />
              </>
            )}
            <Space h="lg" />
          </>

          {AdvizerNavWithAvatar}

          <Space h="lg" />

          <AdvizerProfileCard
            advizerId={advizerId}
            linkToProfilePage={standalone}
            disableTags={disableTags}
          />

          <Space h="xl" />

          {renderAdvizerWatch()}

          <Space h="xl" />
        </Stack>
      </Stack>

      {!standalone && <AdvizerSearchDrawer />}
    </>
  );
};

const AdvizerTags = ({
  advizerId,
  justify,
  disabled,
}: {
  advizerId: string;
  justify?: "center" | "flex-start";
  disabled?: boolean;
}) => {
  const advizer = useAdvizer(advizerId);
  const showMBA = useShowMBA();
  const advizerIsMBA = advizer?.gradDegree === "MBA";

  return (
    <Group w="100%" gap={8} justify={justify || "flex-start"}>
      {showMBA && advizerIsMBA && (
        <AdvizerTag
          key="mba"
          tag={{ id: "MBA", title: "MBA", type: "degree" }}
          size={"md"}
          disableClick={disabled}
        />
      )}
      {advizer?.tags.map((tag) => (
        <AdvizerTag
          key={tag.id}
          tag={tag}
          size={"md"}
          disableClick={disabled}
        />
      ))}
    </Group>
  );
};

const AdvizerPartners = ({ advizerId }: { advizerId: string }) => {
  const advizer = useAdvizer(advizerId);

  const partners = usePartners();

  if (!advizer?.partners || advizer?.partners.length === 0) {
    return null;
  }
  return (
    <Group justify="flex-start" gap="md">
      {(advizer?.partners || []).map((partner) => {
        const partnerConfig = partners?.find((p) => p.id === partner.id);
        return (
          <Group key={partner.id} gap="xs">
            <PartnerLogo
              width={28}
              partnerId={partner.id}
              partnerSlug={partner.slug}
            />
            <Text size="sm" fw={500} c="text.8">
              {partnerConfig?.title || partner.title}
            </Text>
          </Group>
        );
      })}
    </Group>
  );
};

export const AdvizerProfileCard = ({
  advizerId,
  linkToProfilePage,
  disableTags,
}: {
  advizerId: string;
  linkToProfilePage?: boolean;
  disableTags?: boolean;
}) => {
  const advizer = useAdvizer(advizerId);
  const isLaptopOrBigger = useIsLaptopOrBigger();
  const { theme } = useStyles();
  const isRecommendedAdvizer = useIsRecommendedAdvizer({ advizerId });

  const renderRecommendedBadge = useMemo(() => {
    if (!isRecommendedAdvizer) {
      return null;
    }
    return (
      <Badge
        ariaLabel="Recommended Advizer"
        variant="light"
        color="primary"
        size="lg"
        leftSection={<IconStarFilled size={14} />}
        rightSection={<IconStarFilled size={14} />}
      >
        Recommended
      </Badge>
    );
  }, [isRecommendedAdvizer]);

  return (
    <Stack
      gap={0}
      w="100%"
      bg="background.0"
      p={isLaptopOrBigger ? "xl" : "lg"}
      style={{
        borderRadius: 8,
        border: `1px solid ${theme.colors.border[2]}`,
        boxShadow: "0 0 3px 0 rgba(0, 0, 0, 0.1)",
      }}
    >
      <Stack gap={0} w="100%" align="start">
        <Group align="flex-start" justify="space-between" w="100%" flex={1}>
          <Title order={1} size="h2" c="text.8" lh={1.2} ta="left">
            {advizer?.firstName}
          </Title>
          <Group>
            {renderRecommendedBadge}
            {linkToProfilePage && (
              <Link
                to={`/advizers/${advizerId}`}
                target="_blank"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  fontSize: 14,
                }}
              >
                View profile
                <IconExternalLink size={16} />
              </Link>
            )}
            {config.isPartnerApp && (
              <CopyLink
                propsLink={`${window.location.origin}/advizers/${advizerId}`}
                text="Copy student share link"
                context="Advizer Profile Card"
                size="sm"
              />
            )}
          </Group>
        </Group>

        <Space h="sm" />

        <Stack gap={4} w="100%" align="start">
          <Group {...itemGroupProps}>
            <ResourceConfigs.jobFunction.Icon
              size={iconSize}
              color={theme.colors.text[5]}
              style={iconStyle}
            />
            <Text {...itemTitleProps}>{advizer?.jobTitle}</Text>
          </Group>

          {/* <Space h={4} /> */}

          <Group {...itemGroupProps}>
            <ResourceConfigs.company.Icon
              size={iconSize}
              color={theme.colors.text[5]}
              style={iconStyle}
            />
            <Text {...itemTitleProps}>{advizer?.company}</Text>
          </Group>

          <Group {...itemGroupProps}>
            <IconSchool
              size={iconSize}
              color={theme.colors.text[5]}
              style={iconStyle}
            />
            <Text {...itemTitleProps}>
              {(advizer?.undergradSchool || "").replace(
                /WP Carey/g,
                "W.P. Carey",
              )}
              {advizer?.gradProgramsRaw && <br />}
              {(advizer?.gradProgramsRaw || "").replace(
                /WP Carey/g,
                "W.P. Carey",
              )}
            </Text>
          </Group>

          {!!advizer?.personalLinksClean &&
          advizer.personalLinksClean !== "N/A" ? (
            <Group
              {...itemGroupProps}
              onClick={() => {
                Analytics.click({
                  context: "Advizer profile",
                  action: "Personal branding link",
                  advizerId: advizer?.id,
                  advizerName: advizer?.name,
                  partner: advizer?.partners?.map((p) => p.title).join("|"),
                  partnerId: advizer?.partners?.map((p) => p.id).join("|"),
                  partnerSlug: advizer?.partners?.map((p) => p.slug).join("|"),
                });
              }}
            >
              <IconLink
                size={iconSize}
                color={theme.colors.text[5]}
                style={iconStyle}
              />
              <Stack gap="xs" align="flex-start">
                <Text
                  {...itemTitleProps}
                  fw={500}
                  title={advizer?.personalBrandingLinks}
                  lineClamp={5}
                  style={{
                    wordBreak: "break-word",
                  }}
                >
                  {parsePersonalBrandingLinks(
                    advizer?.personalLinksClean || "",
                  )}
                </Text>
                {advizer?.canReachOutToPersonalLinks ? (
                  <Badge
                    color="primary.9"
                    size="md"
                    ariaLabel="This Advizer is open to you reaching out!"
                    leftSection={<IconStar size={14} />}
                  >
                    This Advizer is open to you reaching out!
                  </Badge>
                ) : null}
              </Stack>
            </Group>
          ) : null}

          <Group {...itemGroupProps}>
            <IconUser
              size={iconSize}
              color={theme.colors.text[5]}
              style={iconStyle}
            />
            <Spoiler
              maxHeight={80}
              showLabel="Show more"
              hideLabel="Hide"
              fz={14}
              ta="left"
              c="text.7"
              flex={1}
              styles={{
                content: {
                  fontFamily: "League Spartan, sans-serif",
                  fontSize: 16,
                },
                control: {
                  fontWeight: 400,
                  fontFamily: "League Spartan, sans-serif",
                },
              }}
              // lh={1.3}
            >
              {advizer?.bio}
            </Spoiler>
          </Group>
        </Stack>

        <Space h="xs" />
      </Stack>

      {advizer?.partners?.length ? (
        <>
          <Stack align="start" justify="start" gap="xs" w="100%">
            <AdvizerPartners advizerId={advizerId} />
          </Stack>
          <Space h="sm" />
        </>
      ) : null}

      <Space h="sm" />
      <AdvizerTags advizerId={advizerId} disabled={disableTags} />
    </Stack>
  );
};
